import { InfoCircleFilled } from '@ant-design/icons';
import { Alert, Button, Heading, Spinner, Tooltip } from '@mm/components';
import { EMAIL_MATCH_REGEX } from '@mm/constants/validations';
import { CloseIcon, QuestionCircleIcon } from '@mm/icons';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import { useEmailAccountCRUDActions } from '@mm/state/actions/EmailAccountCRUDActions';
import {
  EmailAccount,
  NewEmailAccount,
} from '@mm/state/interfaces/emailAccount';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { cloneDeep, merge } from 'lodash';
import { EditorState } from 'prosemirror-state';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import PrimarySelect, {
  DropdownOption,
} from '../../../../../../../../common/components/PrimarySelect/PrimarySelect';
import { Editor } from '../../../../../../../components/SlideoutMenu/menus/SlideoutEmail/editor/components/Editor';
import { getHTMLStringFromState } from '../../../../../../../components/SlideoutMenu/menus/SlideoutEmail/editor/utils';
import styles from './EmailAccountEditor.module.css';
import { layers } from '@mm/utils';

export const messageFrequencyOptions: DropdownOption[] = [
  { id: '1', name: '1-2 mins  (720–1,500 messages per day)' },
  { id: '5', name: '3-5 mins (280-720 messages per day)' },
  { id: '10', name: '6-10 mins (140-280 messages per day)' },
];

export const renderMessageFrequency = (fq: string | number) =>
  messageFrequencyOptions.find(opt => opt.id === fq.toString())?.name || fq;

interface Props {
  emailAccountUnderEdit: undefined | NewEmailAccount | EmailAccount;
  onClose?: () => void;
}
const EmailAccountValidationSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
});
const CustomEmailAccountValidationSchema = Yup.object()
  .shape({
    name: Yup.string().required('Required'),
    emailAddress: Yup.string().matches(
      EMAIL_MATCH_REGEX,
      'Invalid email address',
    ),
    credential: Yup.object().shape({
      smtpServerParams: Yup.object().shape({
        address: Yup.string().required('Required'),
        port: Yup.number()
          .typeError('Port must be a number')
          .max(65535, 'Port must be less than 65535')
          .required('Required'),
        userName: Yup.string().required('Required'),
      }),
      incomingServerParams: Yup.object().shape({
        address: Yup.string().required('Required'),
        port: Yup.number()
          .typeError('Port must be a number')
          .max(65535, 'Port must be less than 65535')
          .required('Required'),
        userName: Yup.string().required('Required'),
      }),
    }),
  })
  .test('smtp-password-present', 'Required', value => {
    if (!value.id && !value.credential.smtpServerParams.password) {
      return new Yup.ValidationError(
        'Required',
        undefined,
        'credential.smtpServerParams.password',
      );
    }
    return true;
  })
  .test('incoming-password-present', 'Required', value => {
    if (!value.id && !value.credential.incomingServerParams.password) {
      return new Yup.ValidationError(
        'Required',
        undefined,
        'credential.incomingServerParams.password',
      );
    }
    return true;
  });
export const EmailAccountEditor: FunctionComponent<Props> = props => {
  const { update, create } = useEmailAccountCRUDActions();
  const { retrievePage: retrieveFacebookPage } = useConnectedPageCRUDActions();
  const { closeModal } = useModalActions();

  const { emailAccountUnderEdit, onClose: onCloseProp } = props;

  const signatureValue = emailAccountUnderEdit?.signature || '';
  const [signature, setSignature] = useState<EditorState>();

  const onClose = () => {
    setSignature(undefined);
    onCloseProp?.();
    closeModal();
  };

  const facebookPageId = useSelector(activeFacebookPageIdSelector);

  const [status, setStatus] =
    useState<{ state: 'busy' | 'error'; message: string }>();
  useEffect(() => setStatus(undefined), [emailAccountUnderEdit]);

  const modalActions = useModalActions();

  const [messageFrequency, setMessageFrequency] = useState<
    DropdownOption | undefined
  >();
  useEffect(() => {
    const selected = messageFrequencyOptions.find(
      f => f.id === emailAccountUnderEdit?.messagesFrequency,
    );
    setMessageFrequency(selected);
  }, [emailAccountUnderEdit]);

  const saveAccount = async value => {
    try {
      if (!emailAccountUnderEdit) {
        return;
      }

      const newValue = {
        ...merge(cloneDeep(emailAccountUnderEdit), value),
        messagesFrequency: messageFrequency?.id,
      };

      if (
        emailAccountUnderEdit.kind === 'sendgrid' &&
        emailAccountUnderEdit.credential.accessTokenMasked !==
          value?.credential?.accessTokenMasked
      ) {
        delete newValue.credential.accessTokenMasked;
        newValue.credential.accessToken = value?.credential?.accessTokenMasked;
      }

      if (signature) {
        const html = getHTMLStringFromState(signature);
        newValue.signature = html;
      }

      setStatus({ state: 'busy', message: 'Saving connection...' });
      if ('id' in newValue) {
        if (newValue.credential?.incomingServerParams?.password?.length === 0) {
          delete newValue.credential.incomingServerParams.password;
        }
        if (newValue.credential?.smtpServerParams?.password?.length === 0) {
          delete newValue.credential.smtpServerParams.password;
        }

        await update(newValue);
      } else {
        await create({
          ...newValue,
          facebookPageId,
        });
        await retrieveFacebookPage(facebookPageId);
      }
      onClose();
    } catch (e) {
      if (e instanceof Response) {
        const errorData = await e.json();
        let message = errorData.error;

        if (Array.isArray(errorData.errors) && errorData.errors[0]) {
          message = errorData.errors[0];
        }

        setStatus({ state: 'error', message });
      } else {
        throw e;
      }
    }
  };

  const onSignatureChange = state => {
    setSignature(state);
  };

  const handleClose = () => {
    if (!signature) {
      onClose();
      return;
    }

    const html = getHTMLStringFromState(signature);

    if (signatureValue !== html) {
      modalActions.openGeneralConfirmModal({
        headerText: 'You have unsaved changes',
        messageText: 'Are you sure that you want to leave without saving?',
        onCloseHandler: ok => {
          if (ok) {
            onClose();
          }
        },
        okButtonText: "Yes I'm sure",
        cancelButtonText: 'Cancel',
      });
      return;
    }

    onClose();
  };

  if (!emailAccountUnderEdit) {
    return null;
  }

  const emailAccountSaved =
    'id' in emailAccountUnderEdit && !!emailAccountUnderEdit.id;
  const emailAddressIsGmail =
    emailAccountUnderEdit.emailAddress.endsWith('@gmail.com') &&
    emailAccountUnderEdit.kind === 'gmail';

  const isSendGridAccount = emailAccountUnderEdit.kind === 'sendgrid';

  const isSubmitting = status?.state === 'busy';
  return (
    <div className={styles.container}>
      <div className={styles.overlay} />
      <div className={styles.dialog}>
        <CloseIcon
          className={styles.closeIcon}
          onClick={handleClose}
          color="#262626"
        />
        <Formik
          initialValues={emailAccountUnderEdit}
          onSubmit={saveAccount}
          validationSchema={
            emailAccountUnderEdit.kind === 'custom'
              ? CustomEmailAccountValidationSchema
              : EmailAccountValidationSchema
          }
        >
          {() => (
            <Form className={styles.form}>
              <Heading className={styles.mainHeading} size={3}>
                Email Account Configuration
                <QuestionCircleIcon
                  color="#8C8C8C"
                  className={styles.questionCircleIcon}
                  onClick={() =>
                    window.open(
                      'https://customers.ai/help/l/en/article/z3rhglizny-e-mail-integration',
                    )
                  }
                />
              </Heading>

              <div className={styles.field}>
                <label>Sender Name:</label>
                <div className={styles.fieldEditRow}>
                  <Field
                    disabled={
                      isSubmitting ||
                      emailAccountUnderEdit?.kind === 'microsoft'
                    }
                    type="text"
                    name="name"
                  />
                  {emailAccountUnderEdit?.kind === 'microsoft' && (
                    <Tooltip
                      title={
                        <div>
                          <strong>Why is this disabled?</strong>
                          <br />
                          The sender name and sender email address must be
                          configured in your email system. You may need the
                          assistance of your email system administrator to make
                          changes.
                        </div>
                      }
                      zIndex={Number(layers.layerMax)}
                    >
                      <QuestionCircleIcon />
                    </Tooltip>
                  )}
                </div>

                <ErrorMessage
                  name="name"
                  className={styles.error}
                  component="div"
                />
              </div>

              <div className={styles.field}>
                <label>Email Address:</label>
                <Field
                  disabled={isSubmitting || 'id' in emailAccountUnderEdit}
                  type="text"
                  name="emailAddress"
                />

                <ErrorMessage
                  name="emailAddress"
                  className={styles.error}
                  component="div"
                />
              </div>
              {emailAccountSaved && (
                <div className={styles.field}>
                  <label>Interval between sending emails:</label>
                  {emailAddressIsGmail ? (
                    <div className={styles.info}>
                      <InfoCircleFilled className={styles.infoIcon} />
                      <span>
                        Email accounts ending with @gmail.com are limited to
                        sending 5 emails per day.
                      </span>
                    </div>
                  ) : (
                    <>
                      <PrimarySelect
                        options={messageFrequencyOptions}
                        onChange={opt => setMessageFrequency(opt)}
                        displayNameKey="name"
                        value={messageFrequency}
                        isLoading={isSubmitting}
                        disabled={isSubmitting}
                      />
                      <ErrorMessage
                        name="interval"
                        className={styles.error}
                        component="div"
                      />
                      <div className={styles.info}>
                        <InfoCircleFilled className={styles.infoIcon} />
                        <span>
                          We recommend setting a maximum number of email
                          outreach per day. For example, a maximum of 100 emails
                          per day is highly recommended.
                        </span>
                      </div>
                    </>
                  )}
                </div>
              )}

              {emailAccountSaved && (
                <div className={styles.field}>
                  <label>Signature</label>
                  <Editor
                    value={signatureValue}
                    styleEditor={{ minHeight: 120 }}
                    onChange={onSignatureChange}
                    styleMenubar={{}}
                  />
                </div>
              )}
              {emailAccountSaved && (
                <div className={styles.field}>
                  <label>Unsubscribe link text</label>
                  <Field
                    type="text"
                    disabled={isSubmitting}
                    name="unsubscribeText"
                  />
                </div>
              )}

              {isSendGridAccount && (
                <div className={styles.sendgrid}>
                  <label>SendGrid API Key:</label>
                  <Field
                    type="text"
                    disabled={isSubmitting}
                    name="credential.accessTokenMasked"
                  />
                </div>
              )}

              <div className={styles.custom}>
                {emailAccountUnderEdit.kind === 'custom' && (
                  <>
                    <Heading className={styles.heading} size={4}>
                      Outgoing Mail (SMTP) Server
                    </Heading>

                    <label>Address:</label>
                    <Field
                      type="text"
                      disabled={isSubmitting}
                      name="credential.smtpServerParams.address"
                    />
                    <ErrorMessage
                      name="credential.smtpServerParams.address"
                      className={styles.error}
                      component="div"
                    />

                    <label>Port:</label>
                    <div className={styles.fieldWithCheckbox}>
                      <Field
                        type="text"
                        disabled={isSubmitting}
                        name="credential.smtpServerParams.port"
                      />
                      <Field
                        type="checkbox"
                        disabled={isSubmitting}
                        id="credential.smtpServerParams.enableSsl"
                        name="credential.smtpServerParams.enableSsl"
                      />
                      <label htmlFor="credential.smtpServerParams.enableSsl">
                        Enable SSL
                      </label>
                    </div>
                    <ErrorMessage
                      name="credential.smtpServerParams.port"
                      className={styles.error}
                      component="div"
                    />

                    <label>Username:</label>
                    <Field
                      type="text"
                      disabled={isSubmitting}
                      name="credential.smtpServerParams.userName"
                    />
                    <ErrorMessage
                      name="credential.smtpServerParams.userName"
                      className={styles.error}
                      component="div"
                    />

                    <label>Password:</label>
                    <Field
                      type="password"
                      disabled={isSubmitting}
                      placeholder={'id' in emailAccountUnderEdit ? 'Saved' : ''}
                      name="credential.smtpServerParams.password"
                    />
                    <ErrorMessage
                      name="credential.smtpServerParams.password"
                      className={styles.error}
                      component="div"
                    />

                    <Heading className={styles.heading} size={4} addMarginAbove>
                      Incoming Mail (IMAP) Server
                    </Heading>
                    <label>Address:</label>
                    <Field
                      type="text"
                      disabled={isSubmitting}
                      name="credential.incomingServerParams.address"
                    />
                    <ErrorMessage
                      name="credential.incomingServerParams.address"
                      className={styles.error}
                      component="div"
                    />

                    <label>Port:</label>
                    <div className={styles.fieldWithCheckbox}>
                      <Field
                        type="text"
                        disabled={isSubmitting}
                        name="credential.incomingServerParams.port"
                      />
                      <Field
                        type="checkbox"
                        disabled={isSubmitting}
                        id="credential.incomingServerParams.enableSsl"
                        name="credential.incomingServerParams.enableSsl"
                      />
                      <label htmlFor="credential.incomingServerParams.enableSsl">
                        Enable SSL
                      </label>
                    </div>
                    <ErrorMessage
                      name="credential.incomingServerParams.port"
                      className={styles.error}
                      component="div"
                    />

                    <label>Username:</label>
                    <Field
                      type="text"
                      disabled={isSubmitting}
                      name="credential.incomingServerParams.userName"
                    />
                    <ErrorMessage
                      name="credential.incomingServerParams.userName"
                      className={styles.error}
                      component="div"
                    />

                    <label>Password:</label>

                    <Field
                      type="password"
                      disabled={isSubmitting}
                      placeholder={'id' in emailAccountUnderEdit ? 'Saved' : ''}
                      name="credential.incomingServerParams.password"
                    />
                    <ErrorMessage
                      name="credential.incomingServerParams.password"
                      className={styles.error}
                      component="div"
                    />
                  </>
                )}
              </div>
              {status?.state === 'error' && (
                <Alert
                  className={styles.errorAlert}
                  type="error"
                  showCloseIcon={false}
                >
                  Error: {status.message}
                </Alert>
              )}

              <div className={styles.buttons}>
                <Button
                  color="secondary-blue"
                  onClick={onClose}
                  disabled={isSubmitting}
                  css={{ container: styles.secondaryButton }}
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  css={{ container: styles.primaryButton }}
                >
                  Save Changes
                </Button>

                {status?.state === 'busy' && (
                  <div className={styles.busyIndicator}>
                    <Spinner standalone size={16} width={16} />
                    {status.message}
                  </div>
                )}
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};
